import React from 'react';
import styled from 'styled-components';
import { Progress, Icon, Input, Popover, message } from 'antd';
import { uploadHelper } from '../../help/upload';

import FileChunk from '../../fileChunk/fileChunk';
import {
  completeUpload,
  CreateSignResponse,
  createSigns,
  getExtension,
} from 'common/components/resourceUpload/_helpers/file-chunks-upload';

const ResourceSizeLimit: number = 10 * 1024 * 1024; // 10M

const Layout = styled.div`
  width: 115px;
  margin: 0 12px 12px 0;
  height: 98px;
  position: relative;
`;

const StyledProgress = styled(Progress)`
  & .ant-progress-circle-trail {
    display: none;
  }

  & .ant-progress-text {
    color: #fff;
  }
`;

const Cancel = styled.div<{ show: boolean }>`
  background: red;
  width: 16px;
  height: 16px;
  position: absolute;
  top: 0;
  right: 0;
  z-index: 9;
  border-radius: 0 0 0 100%;
  font-size: 12px;
  display: ${(props) => (props.show ? 'flex' : 'none')};
  justify-content: flex-end;
  align-items: center;
  color: #fff;
  font-weight: bold;
  cursor: pointer;
`;

export interface SquareUploaderProps extends React.Props<undefined> {
  file: File;
  id: string;
  name: string;
  type: any;
  url: string;
  sessionId: string;
  subject: string | 'english' | 'chinese';
  onNameChange?: (value: string, id: string) => void;
  onFileCancel?: (id: string) => void;
  onUUIDChange?: (oldId: string, newId: string) => void;
  onUrlChange?: (id: string, url: string) => void;
  onUploadStart?: () => void;
  onUploadEnd?: () => void;
}

interface SquareUploaderState {
  progress: number;
  reviewUrl: string;
  isError: boolean;
  isPopShow: boolean;
}

interface ResourseUploadInterface {
  file: File;
  chunkIndex: number;
  chunksCount: number;
  sha256: string | number;
  name: string;
  suffix: string;
  index: number;
}

interface UploadResultInterface {
  successList: any[];
  failList: any[];
  result: any;
}

export class SquareUploader extends React.Component<SquareUploaderProps, SquareUploaderState> {
  private xhr: XMLHttpRequest;
  private uploadResult: UploadResultInterface = {
    successList: [],
    failList: [],
    result: null,
  };

  private fileChunk: any;
  private signResponse: CreateSignResponse;
  private responseETags: string[] = [];
  private sha256 = '';

  constructor(props: SquareUploaderProps) {
    super(props);

    this.state = {
      progress: 0,
      reviewUrl: '',
      isError: false,
      isPopShow: false,
    };

    const { name } = this.props;
    const nameParts = name.split('.');
    if (nameParts.length !== 2) {
      message.error('上传失败, 文件名中不能包含多个点号');
      this.cancelHandle();
      return;
    }

    this.checkSha256()
      .then((res: any) => {
        const { url, uuid } = res;
        if (!url && !uuid) {
          const { size } = props.file;
          size > ResourceSizeLimit ? this.bigResourceUpload() : this.uploadFile();
        } else if (!!url && !!uuid) {
          if (this.props.onUrlChange) this.props.onUrlChange(this.props.id, url);
          this.props.onUUIDChange(this.props.id, uuid);
          this.setProgress(100);
        }
      })
      .catch((err: any) => {
        if (err.message === 'ResourceExisted') {
          this.props.onFileCancel(this.props.id);
          message.error('上传失败, 资源已经存在');
        } else {
          this.setState({
            isError: true,
          });
        }
      });
  }

  public componentWillUnmount() {
    if (this.xhr && this.xhr.abort) {
      this.xhr.abort();
    }
  }

  public checkSha256 = () => {
    const { file } = this.props;
    const { name } = file;
    this.fileChunk = new FileChunk(file, 1024 * 1024 * 5);
    return this.fileChunk.init().then((sha256: string) => {
      this.sha256 = String(sha256);
      return this.verifySha256(sha256, name);
    });
  };

  public verifySha256 = (sha256: any, name: string) => {
    return alo7Api.fetch(
      `${alo7Api.prefix.host}/api/v1/resources_${this.props.subject}/check_hash` +
        alo7Nav.objectToSearchString({
          sha256,
          sessionId: this.props.sessionId,
          name,
        })
    );
  };

  public bigResourceUpload = () => {
    const { fileList, fileHash } = this.fileChunk;
    const { name, type } = this.props;
    const suffix = getExtension(name);
    const uploadType = type.toUpperCase();
    const multipartQuantity = fileList.length;
    const signOptions = { suffix, uploadType, multipartQuantity };
    createSigns(signOptions).then((signResponse) => {
      this.signResponse = signResponse;
      this.uploadResource(fileList, fileHash);
    });
  };

  public uploadResourceRequest = (params: ResourseUploadInterface) => {
    const { file, index } = params;
    const url = this.signResponse.uploadUrls[index];
    return fetch(url, { method: 'PUT', body: file })
      .then((res: any) => {
        const etag = res.headers.get('etag');
        this.responseETags[index] = etag.replace(/^"|"$/g, '');
        this.uploadResult.successList.push(params);
        this.setProgress(
          Math.floor((this.uploadResult.successList.length / this.fileChunk.chunksCount) * 100)
        );
        this.uploadResult = {
          ...this.uploadResult,
          result: res,
        };
        return res;
      })
      .catch(() => {
        this.uploadResult.failList.push(params);
        this.setProgress(
          Math.floor((this.uploadResult.successList.length / this.fileChunk.chunksCount) * 100)
        );
      });
  };

  public uploadResource = async (fileList: any[], sha256: string) => {
    try {
      // use promise.all to upload all chunks
      const promises = fileList.map((fileInfo, index) =>
        this.uploadResourceRequest({
          ...fileInfo,
          index,
          sha256,
          sessionId: this.props.sessionId,
        })
      );
      await Promise.all(promises);
    } catch (err) {
      if (err === 'ResourceExisted') {
        this.props.onFileCancel(this.props.id);
      } else {
        this.setState({
          isError: true,
        });
      }
    } finally {
      const { successList, failList } = this.uploadResult;
      const suffix = this.props.name.split('.')[1];
      if (successList.length === this.fileChunk.chunksCount && failList.length === 0) {
        completeUpload({
          uploadType: this.props.type.toUpperCase(),
          suffix,
          eTags: this.responseETags,
          uuid: this.signResponse.uuid,
          name: this.props.name,
          sizeInByte: this.props.file.size,
          sha256: this.sha256,
        }).then((res) => {
          this.props.onUrlChange(this.props.id, res?.url);
          this.props.onUUIDChange(this.props.id, res?.uuid);
          this.setState({ isError: false });
        });
      }
    }
  };

  public uploadFile() {
    this.xhr = uploadHelper(
      {
        file: this.props.file,
        type: this.props.type.toUpperCase(),
        updateProgress: this.setProgress,
        subject: this.props.subject,
        sessionId: this.props.sessionId,
      },
      (res: any) => {
        if (this.props.onUrlChange) {
          this.props.onUrlChange(this.props.id, res.url);
        }
        this.props.onUUIDChange(this.props.id, res.uuid);
      },
      (code: string) => {
        if (code === 'ResourceExisted') {
          this.props.onFileCancel(this.props.id);
        } else {
          this.setState({
            isError: true,
          });
        }
      }
    );
  }

  public setProgress = (value: number) => {
    this.setState({
      progress: value,
    });
    if (value < 10) {
      if (this.props.onUploadStart) this.props.onUploadStart();
    }
    if (value > 98) {
      if (this.props.onUploadEnd) this.props.onUploadEnd();
    }
  };

  public cancelHandle = () => {
    if (this.xhr && this.xhr.abort) {
      this.xhr.abort();
    }
    this.props.onFileCancel(this.props.id);
  };

  public render() {
    const { children, name, id } = this.props;
    const { progress, isError, isPopShow } = this.state;

    return (
      <Popover
        onVisibleChange={(value) => this.setState({ isPopShow: value })}
        placement={'bottom'}
        content={<div style={{ maxWidth: '400px', wordBreak: 'break-all' }}>{name}</div>}>
        <Layout>
          <Cancel show={isPopShow} onClick={this.cancelHandle}>
            <Icon type={'close'} />
          </Cancel>
          <div
            style={{
              height: 'calc(100% - 20px)',
              backgroundColor: '#D9D9D9',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
            }}>
            {children}
            <div
              style={{
                background: 'rgba(0, 0, 0, 0.5)',
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                display: progress > 99 ? (isError ? 'flex' : 'none') : 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
              <div style={{ textAlign: 'center', transform: 'scale(0.9)' }}>
                <StyledProgress
                  status={isError ? 'exception' : progress > 98 ? 'success' : 'normal'}
                  type="circle"
                  percent={progress}
                  width={40}
                  strokeColor={'#ffffff'}
                />
                <div
                  style={{ color: '#fff', fontSize: '12px', cursor: 'pointer' }}
                  onClick={() => {
                    this.setState({
                      progress: 0,
                      isError: false,
                    });
                    this.uploadFile();
                  }}>
                  {progress > 99
                    ? isError
                      ? '上传失败, 重试'
                      : '上传完成'
                    : isError
                      ? '上传失败, 重试'
                      : '上传中...'}
                </div>
              </div>
            </div>
          </div>
          <Input
            size={'small'}
            value={name.replace(/\.[^.]+$/, '')}
            onChange={(e) =>
              this.props.onNameChange(e.target.value + name.match(/\.[^.]+$/)[0], id)
            }
            style={{
              height: '20px',
              fontSize: '12px',
              display: 'block',
              borderRadius: 0,
              border: '1px solid #d9d9d9',
            }}
          />
        </Layout>
      </Popover>
    );
  }
}
